import { WarningRounded } from '@mui/icons-material';
import { Avatar, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { EmptyContent, Spinner, TableHeadCustom, TableNoData, TablePaginationCustom } from '@zeta-core';
import { TrashIcon } from '@zeta-core/assets';
import { getInitials } from './advisor-access-info';

// Interface for tracking deleted users with their timeouts
interface DeletedUserInfo {
  user: any;
  timeoutId: NodeJS.Timeout;
}

interface PaginationProps {
  page: number;
  rowsPerPage: number;
  count: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowsPerPageOptions: number[];
}

export const UserDetailsTable = ({
  isPending,
  tableData,
  handleDeleteUser,
  deletedUser,
  handleUndo,
  pagination,
  canWrite,
}: {
  isPending: boolean;
  tableData: any[];
  handleDeleteUser: (user: any) => void;
  deletedUser: Map<string, DeletedUserInfo>;
  handleUndo: (advisorUserID: string) => void;
  pagination?: PaginationProps;
  canWrite: boolean;
}) => {
  const theme = useTheme();
  const tableLabels = [
    { id: 'name', label: 'User Details', show: true },
    { id: 'actions', label: '', show: true },
  ];

  // Get the list of deleted user IDs
  const deletedUserIDs = Array.from(deletedUser.keys());
  // Get the most recently deleted user (for the notification)
  const lastDeletedUserID = deletedUserIDs.length > 0 ? deletedUserIDs[deletedUserIDs.length - 1] : null;
  const lastDeletedUser = lastDeletedUserID ? deletedUser.get(lastDeletedUserID)?.user : null;

  return (
    <Stack>
      {lastDeletedUser && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mt={2}
          p={2}
          mb={1}
          sx={{ borderRadius: 1, backgroundColor: alpha(theme.palette.warning.lighter, 1) }}
        >
          <Stack direction="row" gap={2} alignItems="center">
            <WarningRounded sx={{ color: 'warning.main' }} />
            <Typography variant="body2" sx={{ color: theme?.palette?.warning?.dark }}>
              User removed.
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            <Button color="warning" size="small" variant="contained" onClick={() => lastDeletedUserID && handleUndo(lastDeletedUserID)}>
              Undo
            </Button>
          </Stack>
        </Stack>
      )}

      <TableContainer>
        <Table>
          <TableHeadCustom headLabel={tableLabels} data={[]} />

          <TableBody>
            {isPending && (
              <TableRow>
                <TableCell colSpan={2}>
                  <EmptyContent title="Loading..." icon={<Spinner />} />
                </TableCell>
              </TableRow>
            )}
            {tableData?.length > 0 &&
              tableData.map((row: any) => (
                <TableRow key={row?.advisorUserID}>
                  <TableCell>
                    <Stack direction="row" gap={2} alignItems="center">
                      <Avatar sx={{ width: 30, height: 30, bgcolor: theme.palette.grey[300], color: theme.palette.text?.secondary, fontSize: '14px' }}>
                        <Typography variant="subtitle2"> {getInitials(row?.fullName)}</Typography>
                      </Avatar>

                      <Stack>
                        <Typography variant="body2" noWrap>
                          {row?.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          {row?.email}
                        </Typography>
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {!row?.disableDeleteIcon && (
                      <IconButton color="error" onClick={() => handleDeleteUser(row)} disabled={!canWrite}>
                        <TrashIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}

            {tableData?.length === 0 && !isPending && <TableNoData notFound />}
          </TableBody>
        </Table>

        {pagination && (
          <TablePaginationCustom
            rowsPerPageOptions={pagination.rowsPerPageOptions}
            count={pagination.count}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.onPageChange}
            onRowsPerPageChange={pagination.onRowsPerPageChange}
          />
        )}
      </TableContainer>
    </Stack>
  );
};
